import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AngularResizeElementDirection, AngularResizeElementEvent } from '../../../../directives/resize-element';
import { EMD_DATE_MAX, EMD_DATE_MIN, EmdChip } from '../emd-mock-data';
import { EmdDateRange } from '../emd-filter.service';

/** ISO 'YYYY-MM-DD' → local-midnight Date (avoids the UTC drift of new Date(iso)). */
function isoToDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Local Date → ISO 'YYYY-MM-DD' (no UTC drift). */
function dateToIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Right slide-in filter panel — the roomier alternate UI over the SAME filter
 * state as the top `emd-filter-bar` chips.
 *
 * Chrome follows the app's own right filter panel, `widget/filter/filter.component`
 * (`app-dynamic-filter`): blue launcher tab at the viewport's right edge, close
 * tab on the panel's left edge, 18/600 header + rule, left-edge drag resize via
 * the in-repo resize-element directive clamped to 350px…(viewport − 180px), thin neutral
 * scrollbar and the `x_mark` Reset chip. Two deliberate departures: it is not a
 * Bootstrap offcanvas (that needs `data-bs-toggle` and exposes no open state the
 * shell can bind, so this uses a transform + `[(open)]`), and the fields are
 * EMD's own `ng-select`s rather than a `formly-form` — EMD has no filter
 * template config to feed formly.
 *
 * Wired to EMD's model: the panel reads the active tab's `EmdChip[]` and writes
 * selections back onto the shared chip objects, emitting `chipChange`, so the bar
 * and panel stay in sync via `EmdFilterService`.
 *
 * Dimension filters use `@ng-select/ng-select` (single for Brand/NDC, multi for
 * the rest) with a two-step Include / Exclude / Clear footer: ticking options
 * builds a per-chip working set; Include commits it as the applied `selected`,
 * Exclude commits its complement, Clear empties the working ticks. Closing a
 * dropdown without applying reverts. The date range is two `mat-datepicker`s
 * (day-grained — EMD data is weekly, so no D/W/M/Q/Y cycle selector); Reset
 * routes through the shell's `EmdFilterService.reset`.
 */
@Component({
    selector: 'emd-filter-panel',
    templateUrl: './emd-filter-panel.component.html',
    styleUrls: ['./emd-filter-panel.component.scss'],
    standalone: false
})
export class EmdFilterPanelComponent implements OnChanges {
  @Input() chips: EmdChip[] = [];
  @Input() dateRange: EmdDateRange = {};
  /** Client brand colour; drives the panel accents via a --emd-accent CSS var. */
  @Input() accentColor?: string;

  /** Two-way open state (shell funnel + panel handle both toggle it). */
  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();

  @Output() chipChange = new EventEmitter<EmdChip>();
  @Output() dateRangeChange = new EventEmitter<EmdDateRange>();
  @Output() reset = new EventEmitter<void>();

  panelWidth = 380;
  itemsShowLimit = 2;
  readonly AngularResizeElementDirection = AngularResizeElementDirection;

  /** Drag bounds, mirroring the reference panel's min-width / max-width. */
  private readonly minPanelWidth = 350;
  private get maxPanelWidth(): number {
    return Math.max(this.minPanelWidth, window.innerWidth - 180);
  }

  /** Working (unapplied) selection, per chip key. */
  private working = new Map<string, string[]>();

  /**
   * Memoised `*ngTemplateOutlet` contexts for the Include/Exclude/Clear footer,
   * per chip key. An inline `{ c: c, sc: sc }` literal would hand
   * NgTemplateOutlet a brand-new object on every change-detection pass, which
   * is the same "binding identity churns forever" trap described on
   * `checkedOf()` below.
   */
  private readonly ieCtx = new Map<string, { c: EmdChip; single: boolean; sc: any }>();

  // ── Date range (day-grained, capped to the extract) ─────────────────────────
  readonly minDate = isoToDate(EMD_DATE_MIN);
  readonly maxDate = isoToDate(EMD_DATE_MAX);
  fromDateObj: Date = this.minDate;
  toDateObj: Date = this.maxDate;
  minFromDate: Date = this.minDate;
  maxFromDate: Date = this.maxDate;
  minToDate: Date = this.minDate;
  maxToDate: Date = this.maxDate;

  // ── Panel open / close / resize ─────────────────────────────────────────────

  /**
   * Seeding has to be input-driven, not only `toggle()`-driven: the panel markup
   * is always in the DOM (it slides in via a transform), the filter bar's funnel
   * button opens it by writing `open` from the shell, and `chips` swaps on every
   * tab change. Anything that leaves `working` unseeded makes `checkedOf()`
   * return a fresh array per change-detection pass — see the note there.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chips']) {
      this.working.clear();
      this.ieCtx.clear();
    }
    if (changes['chips'] || changes['dateRange'] || changes['open']?.currentValue) {
      this.seed();
    }
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) { this.seed(); }
    this.openChange.emit(this.open);
  }

  close(): void {
    this.open = false;
    this.openChange.emit(false);
  }

  /**
   * Clamped, unlike the reference (which stores the raw drag width and leans on
   * CSS min/max): the bound `[style.width.px]` would then keep growing past the
   * rendered edge, and dragging back would do nothing until the stored value
   * re-entered range.
   */
  onResizeSide(e: AngularResizeElementEvent): void {
    this.panelWidth = Math.min(
      Math.max(e.currentWidthValue, this.minPanelWidth),
      this.maxPanelWidth
    );
  }

  /**
   * Snapshot applied state into the working sets + date objects (revert target).
   *
   * Idempotent by design: values are only replaced when they actually differ, so
   * re-seeding never churns the object identities the `[ngModel]` bindings read.
   */
  private seed(): void {
    for (const c of this.chips) {
      const cur = this.working.get(c.key);
      if (!cur || cur.length !== c.selected.length || cur.some((v, i) => v !== c.selected[i])) {
        this.working.set(c.key, [...c.selected]);
      }
    }
    const fromIso = this.dateRange.from ?? EMD_DATE_MIN;
    const toIso = this.dateRange.to ?? EMD_DATE_MAX;
    if (dateToIso(this.fromDateObj) !== fromIso) { this.fromDateObj = isoToDate(fromIso); }
    if (dateToIso(this.toDateObj) !== toIso) { this.toDateObj = isoToDate(toIso); }
    this.minToDate = this.fromDateObj;
    this.maxFromDate = this.toDateObj;
  }

  // ── Dimension working selection + Include / Exclude / Clear ──────────────────

  /**
   * Working ticks for a chip — always the SAME array instance for a given state.
   *
   * This is bound to `[ngModel]` in the template, so it is read on every change
   * detection pass. Returning a fresh `[]` (as an un-seeded lookup used to) makes
   * NgModel see a changed model every pass, and its `_updateValue` microtask
   * calls `markForCheck()`, which schedules another pass — an unbreakable
   * tick loop that pegged the CPU and froze the dashboard the moment it loaded.
   * So a miss seeds the map and stores the array rather than returning a literal.
   */
  checkedOf(c: EmdChip): string[] {
    let arr = this.working.get(c.key);
    if (!arr) {
      arr = [...c.selected];
      this.working.set(c.key, arr);
    }
    return arr;
  }
  setChecked(c: EmdChip, arr: string[]): void { this.working.set(c.key, arr ?? []); }

  /** Stable context object for the Include/Exclude/Clear footer template. */
  ieContext(c: EmdChip, single: boolean, sc: any): { c: EmdChip; single: boolean; sc: any } {
    const key = `${c.key}:${single ? 'single' : 'multi'}`;
    let ctx = this.ieCtx.get(key);
    if (!ctx) {
      ctx = { c, single, sc };
      this.ieCtx.set(key, ctx);
    } else {
      // Same instance, refreshed in place: the chip object and the ng-select ref
      // are stable in practice, but a tab swap can hand us new ones.
      ctx.c = c;
      ctx.sc = sc;
    }
    return ctx;
  }
  onSingleModel(c: EmdChip, val: string | null): void {
    this.working.set(c.key, val ? [val] : []);
  }

  allChecked(c: EmdChip): boolean {
    const n = this.checkedOf(c).length;
    return n > 0 && n === c.options.length;
  }
  toggleSelectAll(c: EmdChip, ev?: Event): void {
    ev?.stopPropagation();
    this.working.set(c.key, this.allChecked(c) ? [] : [...c.options]);
  }
  clearChecked(c: EmdChip, ev?: Event): void {
    ev?.stopPropagation();
    this.working.set(c.key, []);
  }

  canInclude(c: EmdChip): boolean { return this.checkedOf(c).length > 0; }
  canExclude(c: EmdChip): boolean {
    const n = this.checkedOf(c).length;
    return n > 0 && n < c.options.length;
  }

  /** Include shows only the checked; Exclude hides them. Both commit + close. */
  applyInclude(c: EmdChip, sc?: { close?: () => void }): void {
    c.selected = [...this.checkedOf(c)];
    this.chipChange.emit(c);
    sc?.close?.();
  }
  applyExclude(c: EmdChip, sc?: { close?: () => void }): void {
    const set = new Set(this.checkedOf(c));
    c.selected = c.options.filter((o) => !set.has(o));
    this.chipChange.emit(c);
    sc?.close?.();
  }

  /** Closing a dropdown without applying reverts the working set. */
  onSelectClose(c: EmdChip): void {
    this.working.set(c.key, [...c.selected]);
  }

  isPartial(c: EmdChip): boolean { return c.selected.length !== c.options.length; }
  appliedCount(c: EmdChip): number { return c.selected.length; }

  onFilterText(ev: Event): string { return (ev.target as HTMLInputElement).value; }

  // ── Date range ───────────────────────────────────────────────────────────────

  onFromPicked(d: Date | null): void {
    if (!d) { return; }
    this.fromDateObj = d;
    this.minToDate = d;
    this.commitDates();
  }
  onToPicked(d: Date | null): void {
    if (!d) { return; }
    this.toDateObj = d;
    this.maxFromDate = d;
    this.commitDates();
  }
  private commitDates(): void {
    if (!this.fromDateObj || !this.toDateObj) { return; }
    let from = dateToIso(this.fromDateObj);
    let to = dateToIso(this.toDateObj);
    if (from > to) { [from, to] = [to, from]; }
    this.dateRangeChange.emit({ from, to });
  }

  // ── Reset ─────────────────────────────────────────────────────────────────────

  get dateChanged(): boolean { return !!(this.dateRange.from || this.dateRange.to); }
  get anyPartial(): boolean { return this.chips.some((c) => this.isPartial(c)) || this.dateChanged; }

  resetFilters(): void {
    this.reset.emit();
    // The shell has reset the service (chips back to all, date cleared); resync.
    this.seed();
  }
}
